import React, { useState, useEffect } from "react";
import apiClient from "../api/client";
import { useNavigate } from "react-router-dom";
import { useDistanceUnit } from "../contexts/DistanceUnitContext";
import Sidebar from "./Sidebar";
import FriendProfileModal from "./FriendProfileModal";
import "./Leaderboard.css";
import iconMenu from "../assets/icon-menu.svg";
import iconSearch from "../assets/icon-search.svg";
import NotificationBell from "./NotificationBell";
import ProfilePicture from "./ProfilePicture";

const Leaderboard = ({ user, onLogout }) => {
  const [friendsStats, setFriendsStats] = useState([]);
  const [publicStats, setPublicStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('friends'); // 'friends' or 'global'
  const [sortBy, setSortBy] = useState('current_streak');
  const [countryFilter, setCountryFilter] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const { convertDistance, getUnitLabel } = useDistanceUnit();
  const navigate = useNavigate();

  useEffect(() => {
    fetchFriendsStats();
    fetchPublicStats();
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await apiClient.get('/profile');
      setProfileData(response.data.profile);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const fetchFriendsStats = async () => {
    try {
      const response = await apiClient.get('/friends/stats');

      const friendsData = Array.isArray(response.data?.friends_stats)
        ? response.data.friends_stats
        : [];
      setFriendsStats(friendsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching friends stats:", error);
      setFriendsStats([]);
      setLoading(false);
    }
  };

  const fetchPublicStats = async () => {
    try {
      const response = await apiClient.get('/public/stats');

      const publicData = Array.isArray(response.data?.public_stats)
        ? response.data.public_stats
        : [];
      setPublicStats(publicData);
    } catch (error) {
      console.error("Error fetching public stats:", error);
      setPublicStats([]);
    }
  };

  const convertMilesToUnit = (miles) => {
    return convertDistance(miles * 1609.34); // Convert miles to meters first
  };

  const normalizedFriendsStats = Array.isArray(friendsStats) ? friendsStats : [];
  const normalizedPublicStats = Array.isArray(publicStats) ? publicStats : [];

  const filteredPublicStats = countryFilter
    ? normalizedPublicStats.filter(stat => stat.country === countryFilter)
    : normalizedPublicStats;

  const currentStats = activeTab === 'friends' ? normalizedFriendsStats : filteredPublicStats;

  const sortedStats = currentStats.length
    ? [...currentStats].sort((a, b) => {
        switch (sortBy) {
          case 'current_streak':
            return b.current_streak - a.current_streak;
          case 'longest_streak':
            return b.longest_streak - a.longest_streak;
          case 'total_run_days':
            return b.total_run_days - a.total_run_days;
          case 'total_distance':
            return b.total_distance_miles - a.total_distance_miles;
          default:
            return 0;
        }
      })
    : [];

  const availableCountries = [
    ...new Set(normalizedPublicStats.map(stat => stat.country).filter(Boolean))
  ].sort();

  const getBadge = (stat) => {
    // Determine badge based on stats
    if (stat.current_streak >= 100) return { text: "Elite", className: "elite" };
    if (stat.current_streak >= 50) return { text: "Consistent", className: "consistent" };
    if (stat.total_run_days >= 200) return { text: "Dedicated", className: "dedicated" };
    if (stat.total_run_days >= 50) return { text: "Growing", className: "growing" };
    return { text: "New", className: "new" };
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  if (loading) {
    return (
      <div className="leaderboard-page">
        <Sidebar isOpen={sidebarOpen} />
        <div className={`leaderboard-main-container ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
          <div className="leaderboard-loading">Loading leaderboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-page">
      <Sidebar isOpen={sidebarOpen} />

      <div className={`leaderboard-main-container ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <button className="icon-button" onClick={toggleSidebar}>
              <img src={iconMenu} alt="Menu" />
            </button>
            <h1 className="header-title">Runstreak Dashboard</h1>
          </div>
          <div className="header-right">
            <div className="header-search-container">
              <img src={iconSearch} alt="Search" className="search-icon-header" />
              <input
                type="text"
                placeholder="Search"
                className="header-search-input"
              />
            </div>
            <NotificationBell />
            <button className="logout-button" onClick={onLogout}>Logout</button>
            <div onClick={() => navigate('/profile')} style={{ cursor: 'pointer' }}>
              <ProfilePicture
                profilePictureUrl={profileData?.profile_picture_url}
                firstName={profileData?.first_name}
                lastName={profileData?.last_name}
                email={user?.email}
                size="small"
                showInitials={true}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="leaderboard-content">
          {/* Page Header */}
          <div className="leaderboard-header">
            <div className="leaderboard-header-left">
              <h1>Leaderboard</h1>
              <p>Compare your running stats with friends and the community</p>
            </div>
            <div className="leaderboard-header-right">
              <select
                className="leaderboard-sort-dropdown"
                value={sortBy}
                onChange={handleSortChange}
              >
                <option value="current_streak">Current Streak</option>
                <option value="longest_streak">Longest Streak</option>
                <option value="total_run_days">Total Days</option>
                <option value="total_distance">Total Distance</option>
              </select>
            </div>
          </div>

          {/* Tabs */}
          <div className="leaderboard-tabs-container">
            <div className="leaderboard-tabs">
              <button
                className={`leaderboard-tab ${activeTab === 'friends' ? 'active' : ''}`}
                onClick={() => setActiveTab('friends')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 7C7.66 7 9 5.66 9 4C9 2.34 7.66 1 6 1C4.34 1 3 2.34 3 4C3 5.66 4.34 7 6 7ZM10 7C11.66 7 13 5.66 13 4C13 2.34 11.66 1 10 1C9.68 1 9.38 1.08 9.1 1.2C9.66 2.06 10 3 10 4C10 5 9.66 5.94 9.1 6.8C9.38 6.92 9.68 7 10 7ZM6 8.5C3.66 8.5 1 9.66 1 12V14H11V12C11 9.66 8.34 8.5 6 8.5ZM11 8.5C10.76 8.5 10.5 8.52 10.22 8.56C11.26 9.32 12 10.28 12 12V14H15V12C15 9.66 12.34 8.5 11 8.5Z" fill="currentColor"/>
                </svg>
                Friends ({normalizedFriendsStats.length})
              </button>
              <button
                className={`leaderboard-tab ${activeTab === 'global' ? 'active' : ''}`}
                onClick={() => setActiveTab('global')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1C4.14 1 1 4.14 1 8C1 11.86 4.14 15 8 15C11.86 15 15 11.86 15 8C15 4.14 11.86 1 8 1ZM13.64 5.5H11.54C11.34 4.66 11.04 3.86 10.66 3.12C11.86 3.62 12.88 4.46 13.64 5.5ZM8 2.54C8.64 3.34 9.16 4.36 9.46 5.5H6.54C6.84 4.36 7.36 3.34 8 2.54ZM2.66 9.5C2.54 9.02 2.5 8.52 2.5 8C2.5 7.48 2.54 6.98 2.66 6.5H5.12C5.06 6.98 5 7.48 5 8C5 8.52 5.06 9.02 5.12 9.5H2.66ZM3.36 10.5H5.46C5.66 11.34 5.96 12.14 6.34 12.88C5.14 12.38 4.12 11.54 3.36 10.5ZM5.46 5.5H3.36C4.12 4.46 5.14 3.62 6.34 3.12C5.96 3.86 5.66 4.66 5.46 5.5ZM8 13.46C7.36 12.66 6.84 11.64 6.54 10.5H9.46C9.16 11.64 8.64 12.66 8 13.46ZM9.82 9.5H6.18C6.12 9.02 6 8.52 6 8C6 7.48 6.12 6.98 6.18 6.5H9.82C9.88 6.98 10 7.48 10 8C10 8.52 9.88 9.02 9.82 9.5ZM10.66 12.88C11.04 12.14 11.34 11.34 11.54 10.5H13.64C12.88 11.54 11.86 12.38 10.66 12.88ZM10.88 9.5C10.94 9.02 11 8.52 11 8C11 7.48 10.94 6.98 10.88 6.5H13.34C13.46 6.98 13.5 7.48 13.5 8C13.5 8.52 13.46 9.02 13.34 9.5H10.88Z" fill="currentColor"/>
                </svg>
                Global
              </button>
            </div>
          </div>

          {/* Leaderboard Card */}
          <div className="leaderboard-card">
            {/* Card Header */}
            <div className="leaderboard-card-header">
              <div className="leaderboard-card-title">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 1L12.245 6.91L18.532 7.545L13.906 11.59L15.451 17.755L10 14.545L4.549 17.755L6.094 11.59L1.468 7.545L7.755 6.91L10 1Z" fill="#14b8a6" stroke="#14b8a6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h2>{activeTab === 'friends' ? 'Friends Leaderboard' : 'Global Leaderboard'}</h2>
              </div>
              <p className="leaderboard-card-description">
                {activeTab === 'friends'
                  ? 'Compare with friends who have enabled stats sharing'
                  : 'All runners who share their stats publicly'
                }
              </p>
            </div>

            {/* Country Filter for Global Tab */}
            {activeTab === 'global' && (
              <div className="leaderboard-country-filter">
                <label>Country:</label>
                <select
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                >
                  <option value="">All Countries ({normalizedPublicStats.length})</option>
                  {availableCountries.map(country => (
                    <option key={country} value={country}>
                      {country} ({normalizedPublicStats.filter(s => s.country === country).length})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Leaderboard List */}
            <div className="leaderboard-list">
              {sortedStats.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#717182', padding: '40px' }}>
                  {activeTab === 'friends'
                    ? 'No friends found. Add friends and enable stats sharing!'
                    : 'No public stats available.'}
                </p>
              ) : (
                sortedStats.map((stat, index) => (
                  <div
                    key={stat.user_id}
                    className={`leaderboard-item ${stat.is_current_user ? 'current-user' : ''}`}
                    onClick={() => setSelectedUserId(stat.user_id)}
                  >
                    {/* Rank */}
                    <div className={`leaderboard-rank ${index < 3 ? 'medal' : 'number'}`}>
                      {index === 0 && <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23FFD700'%3E%3Cpath d='M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z'/%3E%3C/svg%3E" alt="Gold" />}
                      {index === 1 && <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23C0C0C0'%3E%3Cpath d='M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z'/%3E%3C/svg%3E" alt="Silver" />}
                      {index === 2 && <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23CD7F32'%3E%3Cpath d='M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z'/%3E%3C/svg%3E" alt="Bronze" />}
                      {index > 2 && <span>{index + 1}</span>}
                    </div>

                    {/* Avatar */}
                    <div className="leaderboard-avatar">
                      <ProfilePicture
                        profilePictureUrl={stat.profile_picture_url}
                        firstName={stat.first_name}
                        lastName={stat.last_name}
                        email={stat.email || stat.username}
                        size="medium"
                        className="leaderboard-avatar-image"
                        showInitials={true}
                      />
                    </div>

                    {/* User Info */}
                    <div className="leaderboard-user-info">
                      <div className="leaderboard-user-name-row">
                        <span className="leaderboard-user-name">
                          {stat.first_name && stat.last_name
                            ? `${stat.first_name} ${stat.last_name}`
                            : stat.username}
                        </span>
                        {stat.is_current_user && (
                          <span className="leaderboard-badge you">You</span>
                        )}
                        {!stat.is_current_user && (
                          <span className={`leaderboard-badge ${getBadge(stat).className}`}>
                            {getBadge(stat).text}
                          </span>
                        )}
                      </div>
                      <div className="leaderboard-user-meta">
                        {activeTab === 'global' && stat.country && (
                          <span className="leaderboard-user-meta-item">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path d="M6 1C3.24 1 1 3.24 1 6C1 8.76 3.24 11 6 11C8.76 11 11 8.76 11 6C11 3.24 8.76 1 6 1Z" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                            </svg>
                            {stat.country}
                          </span>
                        )}
                        {stat.created_at && (
                          <span className="leaderboard-user-meta-item">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <rect x="2" y="2.5" width="8" height="7" rx="1" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                              <path d="M2 4.5H10M4 2.5V1.5M8 2.5V1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            </svg>
                            Joined {new Date(stat.created_at).getFullYear()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="leaderboard-stats">
                      <div className="leaderboard-stat">
                        <div className={`leaderboard-stat-value ${stat.is_current_user ? 'current' : 'other'}`}>
                          {stat.current_streak}
                        </div>
                        <div className="leaderboard-stat-label">Current</div>
                      </div>
                      <div className="leaderboard-stat">
                        <div className={`leaderboard-stat-value ${stat.is_current_user ? 'current' : 'other'}`}>
                          {stat.longest_streak}
                        </div>
                        <div className="leaderboard-stat-label">Longest</div>
                      </div>
                      <div className="leaderboard-stat">
                        <div className={`leaderboard-stat-value ${stat.is_current_user ? 'current' : 'other'}`}>
                          {stat.total_run_days || 0}
                        </div>
                        <div className="leaderboard-stat-label">Total Days</div>
                      </div>
                      <div className="leaderboard-stat">
                        <div className={`leaderboard-stat-value ${stat.is_current_user ? 'current' : 'other'}`}>
                          {Math.round(convertMilesToUnit(stat.total_distance_miles))}
                        </div>
                        <div className="leaderboard-stat-label">Total {getUnitLabel()}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Friend Profile Modal */}
      {selectedUserId && (
        <FriendProfileModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
};

export default Leaderboard;
